import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/AuthContext';
import HomePage from './components/home/HomePage';
import Login from './components/Login';
import QuizSelection from './components/QuizSelection';
import { QuizAttempt } from './components/QuizAttempt';
import Students from './components/Navbar/services/Students';
import JobSeekers from './components/Navbar/services/JobSeekers';
import SelfAwareness from './components/Navbar/services/SelfAwareness';
import Organization from './components/Navbar/services/Organization';
import BankingSolutions from './components/Navbar/services/BankingSolutions';
import Contact from './components/Navbar/Contact';
import ResultPage from './components/ResultPage';
import AdminQuizzes from "./components/admin/AdminQuizzes";

const LoadingScreen = () => (
    <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        flexDirection: 'column',
        gap: '20px'
    }}>
        <div>Loading...</div>
        <div style={{ fontSize: '14px', color: '#666' }}>
            Please wait while we load your content
        </div>
    </div>
);

// Protected route wrapper component
const ProtectedRoute = ({ children }) => {
    const { user, authReady } = useAuth();

    if (!authReady) {
        return <LoadingScreen />;
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    return children;
};

// Admin route wrapper component
const AdminRoute = ({ children }) => {
    const { user, isAdmin, authReady, adminLoaded } = useAuth();

    if (!authReady) {
        return <LoadingScreen />;
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Wait for admin flag only on admin routes
    if (!adminLoaded) {
        return <LoadingScreen />;
    }

    if (!isAdmin) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                fontSize: '18px',
                flexDirection: 'column',
                gap: '20px'
            }}>
                <div>Access Denied</div>
                <div style={{ fontSize: '14px', color: '#666' }}>
                    You don't have admin privileges
                </div>
                <button onClick={() => window.location.href = '/'}>
                    Go Home
                </button>
            </div>
        );
    }

    return children;
};

// Public route wrapper component
const PublicRoute = ({ children }) => {
    const { user, authReady } = useAuth();

    if (!authReady) {
        return <LoadingScreen />;
    }

    if (user) {
        return <Navigate to="/" replace />;
    }

    return children;
};

// Main app routes component
const AppRoutes = () => {
    const { authReady } = useAuth();

    if (!authReady) {
        return <LoadingScreen />;
    }

    return (
        <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <Login />
                    </PublicRoute>
                }
            />

            {/* Protected routes */}
            <Route
                path="/quiz-selection"
                element={
                    <ProtectedRoute>
                        <QuizSelection />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/quiz"
                element={
                    <ProtectedRoute>
                        <QuizAttempt />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/result"
                element={
                    <ProtectedRoute>
                        <ResultPage />
                    </ProtectedRoute>
                }
            />

            {/* Admin routes */}
            <Route
                path="/admin/quizzes"
                element={
                    <AdminRoute>
                        <AdminQuizzes />
                    </AdminRoute>
                }
            />

            {/* Public service routes */}
            <Route path="/contact" element={<Contact />} />
            <Route path="/students" element={<Students />} />
            <Route path="/job-seekers" element={<JobSeekers />} />
            <Route path="/self-awareness" element={<SelfAwareness />} />
            <Route path="/organization" element={<Organization />} />
            <Route path="/banking-solutions" element={<BankingSolutions />} />

            {/* Catch all route - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};

function App() {
    return (
        <AuthProvider>
            <Router>
                <AppRoutes />
            </Router>
        </AuthProvider>
    );
}

export default App;